/** A report's Schedules tab: your schedules of it, to pause, resume or delete. */
import { useState } from 'react';
import {
  Badge,
  Banner,
  Button,
  Modal,
  Skeleton,
  Table,
} from '@servicecompressionllc/sc-design-system';
import { REPORT_PERIODS } from '../workflowBuilder/steps/ReportStepEditor.js';
import { schedulePhrase, summaryText } from '../workflowBuilder/summarize.js';
import { useReportSchedules, type ReportSchedule } from './useReportSchedules.js';

const periodLabel = (period: string): string =>
  REPORT_PERIODS.find((p) => p.value === period)?.label ?? period;

const recipientsLabel = (s: ReportSchedule): string =>
  s.recipients.length === 0 ? 'Nobody' : s.recipients.map((r) => r.name).join(', ');

export const ReportSchedulesTab = ({ templateId }: { templateId: string }) => {
  const { schedules, setPaused, remove } = useReportSchedules(templateId, true);
  const [deleting, setDeleting] = useState<ReportSchedule | null>(null);
  const error = setPaused.error ?? remove.error;
  const closeDelete = () => {
    setDeleting(null);
    remove.reset();
  };

  return (
    <section aria-label="Schedules" className="lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
      {error && !deleting && (
        <Banner variant="danger" className="mb-4">
          <Banner.Body>
            <Banner.Title>{error.message}</Banner.Title>
          </Banner.Body>
        </Banner>
      )}
      {schedules.isLoading && <Skeleton className="h-40 w-full" />}
      {schedules.error && (
        <Banner variant="danger">
          <Banner.Body>
            <Banner.Title>{schedules.error.message}</Banner.Title>
          </Banner.Body>
        </Banner>
      )}
      {schedules.data?.length === 0 && (
        <div className="rounded-lg border border-outline bg-surface-elevated py-12 text-center">
          <p className="text-ink-muted">You haven&apos;t scheduled this report.</p>
          <p className="mt-1 text-xs text-ink-subtle">
            Ask the assistant in chat, for example: &ldquo;Run this report every Monday at 6 AM and
            send me the link.&rdquo;
          </p>
        </div>
      )}
      {(schedules.data?.length ?? 0) > 0 && (
        <>
          <div className="overflow-x-auto rounded-lg border border-outline">
            <Table.Root className="w-full text-sm">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Runs</Table.HeaderCell>
                  <Table.HeaderCell>Period</Table.HeaderCell>
                  <Table.HeaderCell>Units</Table.HeaderCell>
                  <Table.HeaderCell>Link goes to</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell aria-label="Actions" />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {(schedules.data ?? []).map((s) => (
                  <Table.Row key={s.id}>
                    <Table.Cell>{summaryText(schedulePhrase(s.schedule))}</Table.Cell>
                    <Table.Cell className="text-ink-muted">{periodLabel(s.period)}</Table.Cell>
                    <Table.Cell className="text-ink-muted">
                      {[s.customer, s.region, s.units].filter(Boolean).join(', ') ||
                        'The report’s own filters'}
                    </Table.Cell>
                    <Table.Cell className="text-ink-muted">{recipientsLabel(s)}</Table.Cell>
                    <Table.Cell>
                      <Badge variant={s.paused ? 'neutral' : 'success'}>
                        {s.paused ? 'Paused' : 'Active'}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell align="right" className="whitespace-nowrap">
                      <span className="inline-flex gap-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={setPaused.isPending}
                          onClick={() => setPaused.mutate({ id: s.id, paused: !s.paused })}
                        >
                          {s.paused ? 'Resume' : 'Pause'}
                        </Button>
                        <Button variant="danger" size="sm" onClick={() => setDeleting(s)}>
                          Delete
                        </Button>
                      </span>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </div>
          <p className="mt-2 text-xs text-ink-subtle">
            Each run shows up under Your runs. Resuming skips any time missed while paused.
          </p>
        </>
      )}

      <Modal open={deleting !== null} onOpenChange={(open) => !open && closeDelete()}>
        <Modal.Header>
          <Modal.Title>Delete this schedule?</Modal.Title>
          <Modal.CloseButton />
        </Modal.Header>
        <Modal.Body>
          <p className="text-sm text-ink">
            {deleting ? summaryText(schedulePhrase(deleting.schedule)) : ''}. It won&apos;t run
            again; runs it already made stay under Your runs.
          </p>
          {remove.error && (
            <p role="alert" className="mt-2 text-xs text-danger">
              {remove.error.message}
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeDelete}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={remove.isPending}
            onClick={() => deleting && remove.mutate(deleting.id, { onSuccess: closeDelete })}
          >
            {remove.isPending ? 'Deleting' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
};
